import { legalArmIndices, matchValueFor } from '../game/Chain.js';
import { ALL_PIPS, type Pip, type Tile } from '../game/types.js';
import type { GameStateData } from '../game/GameState.js';

/**
 * Per-suit visibility from the AI's perspective. For each pip value 0..6:
 *  - `inHand`    — count of half-tiles of that pip in the AI's own hand
 *  - `onChain`   — count of half-tiles of that pip played on the chain
 *  - `unseen`    — count remaining hidden (opponent hands + boneyard)
 *
 * For the double-six set every pip appears 8 times across the 28 tiles
 * (7 tiles touch each pip; the double contributes 2 half-tiles). So
 * `inHand + onChain + unseen === 8` for every pip.
 *
 * Feeds the evaluator's H3 / H4 (count-band shaping) and H6 (defensive
 * blocking) — leaving the opponent on a pip with many unseen copies is
 * good for *them*; leaving them on a pip whose copies are mostly in our
 * hand or already on the chain starves them.
 */
export interface SuitCounts {
  readonly inHand: number;
  readonly onChain: number;
  readonly unseen: number;
}

const TOTAL_PER_PIP = 8;

type MutableCounts = Record<Pip, { inHand: number; onChain: number }>;

function blankCounts(): MutableCounts {
  const out = {} as MutableCounts;
  for (const p of ALL_PIPS) out[p] = { inHand: 0, onChain: 0 };
  return out;
}

function addTile(counts: MutableCounts, tile: Tile, where: 'inHand' | 'onChain'): void {
  // Doubles contribute 2 half-tiles to the same pip; non-doubles
  // contribute one to each half. Same code works for both.
  counts[tile[0]][where] += 1;
  counts[tile[1]][where] += 1;
}

/**
 * Compute per-pip counts from the AI's perspective. Sums tiles in own hand
 * + tiles played across every chain arm; `unseen` is whatever remains.
 *
 * Important: `Chain.applyLead` stores the lead tile in BOTH initial arm
 * references (or all 4 for a spinner-lead, where arms 0/1 are the same
 * `mainAxis` ref). Iterating arm tiles therefore double-counts the lead's
 * half-tiles — we subtract one copy at the end.
 */
export function suitCountsFor(state: GameStateData, seat: number): Record<Pip, SuitCounts> {
  const partial = blankCounts();

  const hand = state.hands[seat] ?? [];
  for (const tile of hand) addTile(partial, tile, 'inHand');

  for (const arm of state.chain.arms) {
    for (const tile of arm.tiles) addTile(partial, tile, 'onChain');
  }
  if (state.chain.lead !== null) {
    const lead = state.chain.lead;
    partial[lead[0]].onChain -= 1;
    partial[lead[1]].onChain -= 1;
  }

  const out = {} as Record<Pip, SuitCounts>;
  for (const p of ALL_PIPS) {
    const { inHand, onChain } = partial[p];
    out[p] = { inHand, onChain, unseen: TOTAL_PER_PIP - inHand - onChain };
  }
  return out;
}

/**
 * Currently open pip values across all *legal* active arms (deduplicated).
 * "Legal" filters out perpendicular arms of an uncovered spinner (their
 * openValue carries the spinner face but no play can land there yet) —
 * otherwise pass-inference [[passconstraints]] would wrongly attribute
 * "couldn't hold pip = spinner face" to passers.
 */
export function openPipValues(state: GameStateData): readonly Pip[] {
  const seen = new Set<Pip>();
  const legal = new Set(legalArmIndices(state.chain));
  for (const idx of state.chain.activeArms) {
    if (!legal.has(idx)) continue;
    const arm = state.chain.arms[idx];
    if (!arm) continue;
    // matchValueFor returns the face of a double-outer (where openValue
    // carries the pip-sum for scoring) and arm.openValue otherwise.
    const v = matchValueFor(arm);
    if (v >= 0 && v <= 6 && Number.isInteger(v)) {
      seen.add(v as Pip);
    }
  }
  return Array.from(seen);
}
